import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'uniquepaths', label: 'Unique Paths' },
  { key: 'lcs', label: 'Longest Common Subseq.' },
  { key: 'minpathsum', label: 'Minimum Path Sum' },
  { key: 'editdistance', label: 'Edit Distance' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

const GridBoxes = ({ label, grid, getCellState }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--muted)', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, overflowX: 'auto' }}>
      {grid.map((row, r) => (
        <div key={r} style={{ display: 'flex', gap: 4 }}>
          {row.map((cell, c) => {
            const st = getCellState ? getCellState(r, c) : {};
            return (
              <div key={c} style={{
                width: 42, height: 42, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                fontSize: 13, fontWeight: 800, fontFamily: "'Inter',sans-serif",
                background: st.bg || 'var(--bg3)', color: st.color || (cell === null || cell === undefined ? 'var(--muted)' : 'var(--text)'),
                border: `1.5px solid ${st.border || 'var(--border)'}`, transition: 'all 0.2s ease',
              }}>{cell === null || cell === undefined ? '·' : cell}</div>
            );
          })}
        </div>
      ))}
    </div>
  </div>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 220 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={{
      padding: '8px 14px', borderRadius: 9, cursor: step === 0 ? 'default' : 'pointer',
      border: `1.5px solid ${step === 0 ? 'var(--border)' : V1}`, background: 'transparent',
      color: step === 0 ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
      fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: step === 0 ? 0.6 : 1,
    }}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);

const getFromCellState = (cur, r, c) => {
  if (!cur) return {};
  if (cur.cell && cur.cell[0] === r && cur.cell[1] === c) return { bg: `linear-gradient(135deg,${V1},${V2})`, border: V1, color: '#fff' };
  if (cur.from && cur.from.some(([fr, fc]) => fr === r && fc === c)) return { bg: `${TEAL}22`, border: TEAL, color: 'var(--text)' };
  return {};
};

/* ─── Unique Paths ────────────────────────────────────────────────── */
const UniquePathsMode = () => {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const m = Math.min(5, Math.max(1, rows));
    const n = Math.min(5, Math.max(1, cols));
    const dp = Array.from({ length: m }, () => new Array(n).fill(null));
    const built = [];
    for (let i = 0; i < m; i++) {
      for (let j = 0; j < n; j++) {
        if (i === 0 || j === 0) {
          dp[i][j] = 1;
          built.push({ grid: dp.map(r => [...r]), cell: [i, j], note: `dp[${i}][${j}] = 1 — along the top edge or left edge, there's only one way to arrive (a straight line of moves).` });
        } else {
          dp[i][j] = dp[i - 1][j] + dp[i][j - 1];
          built.push({ grid: dp.map(r => [...r]), cell: [i, j], from: [[i - 1, j], [i, j - 1]], note: `dp[${i}][${j}] = dp[${i - 1}][${j}] + dp[${i}][${j - 1}] = ${dp[i - 1][j]} + ${dp[i][j - 1]} = ${dp[i][j]}.` });
        }
      }
    }
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const m = Math.min(5, Math.max(1, rows)), n = Math.min(5, Math.max(1, cols));
  const emptyGrid = Array.from({ length: m }, () => new Array(n).fill(null));

  const code = {
    lines: [
      'int uniquePaths(int m, int n) {',
      '    int[][] dp = new int[m][n];',
      '    for (int i = 0; i < m; i++) {',
      '        for (int j = 0; j < n; j++) {',
      '            dp[i][j] = (i == 0 || j == 0)',
      '                ? 1',
      '                : dp[i - 1][j] + dp[i][j - 1];',
      '        }',
      '    }',
      '    return dp[m - 1][n - 1];',
      '}',
    ],
    active: !cur ? -1 : (cur.cell[0] === 0 || cur.cell[1] === 0) ? 5 : 6,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GridBoxes label={`dp grid (${m}×${n}) — number of routes to each cell`} grid={cur ? cur.grid : emptyGrid} getCellState={(r, c) => getFromCellState(cur, r, c)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>rows</span>
          <input value={rows} onChange={e => setRows(Math.min(5, Math.max(1, parseInt(e.target.value, 10) || 1)))} style={{ ...smallInput, width: 55 }} type="number" min={1} max={5} />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>cols</span>
          <input value={cols} onChange={e => setCols(Math.min(5, Math.max(1, parseInt(e.target.value, 10) || 1)))} style={{ ...smallInput, width: 55 }} type="number" min={1} max={5} />
          <button onClick={run} style={smallBtn(V1)}>Build Grid</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Choose a grid size (up to 5×5), then Build Grid — watch each cell sum its neighbor above and to the left.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Unique Paths (Grid DP)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Longest Common Subsequence ──────────────────────────────────── */
const LcsMode = () => {
  const [text1, setText1] = useState('abcde');
  const [text2, setText2] = useState('ace');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const s1 = text1.slice(0, 6);
    const s2 = text2.slice(0, 6);
    if (s1.length === 0 || s2.length === 0) return;
    const m = s1.length, n = s2.length;
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    const built = [{ grid: dp.map(r => [...r]), cell: [0, 0], note: 'Base row and column are all 0 — an empty prefix shares no characters with anything.' }];
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (s1[i - 1] === s2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
          built.push({ grid: dp.map(r => [...r]), cell: [i, j], from: [[i - 1, j - 1]], note: `'${s1[i - 1]}' == '${s2[j - 1]}' — match! dp[${i}][${j}] = dp[${i - 1}][${j - 1}] + 1 = ${dp[i - 1][j - 1]} + 1 = ${dp[i][j]}.` });
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
          built.push({ grid: dp.map(r => [...r]), cell: [i, j], from: [[i - 1, j], [i, j - 1]], note: `'${s1[i - 1]}' ≠ '${s2[j - 1]}' — no match. dp[${i}][${j}] = max(dp[${i - 1}][${j}], dp[${i}][${j - 1}]) = max(${dp[i - 1][j]}, ${dp[i][j - 1]}) = ${dp[i][j]}.` });
        }
      }
    }
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const s1 = text1.slice(0, 6), s2 = text2.slice(0, 6);
  const emptyGrid = Array.from({ length: s1.length + 1 }, () => new Array(s2.length + 1).fill(0));

  const code = {
    lines: [
      'int longestCommonSubsequence(String text1, String text2) {',
      '    int m = text1.length(), n = text2.length();',
      '    int[][] dp = new int[m + 1][n + 1];',
      '    for (int i = 1; i <= m; i++) {',
      '        for (int j = 1; j <= n; j++) {',
      '            if (text1.charAt(i - 1) == text2.charAt(j - 1)) {',
      '                dp[i][j] = dp[i - 1][j - 1] + 1;',
      '            } else {',
      '                dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);',
      '            }',
      '        }',
      '    }',
      '    return dp[m][n];',
      '}',
    ],
    active: !cur ? -1 : (cur.from && cur.from.length === 1) ? 6 : 8,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GridBoxes label={`dp grid — LCS length of each prefix pair`} grid={cur ? cur.grid : emptyGrid} getCellState={(r, c) => getFromCellState(cur, r, c)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={text1} onChange={e => { setText1(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 110 }} placeholder="abcde (max 6)" />
          <input value={text2} onChange={e => { setText2(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 110 }} placeholder="ace (max 6)" />
          <button onClick={run} style={smallBtn(V1)}>Build Grid</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter two short strings (max 6 chars each), then Build Grid.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Longest Common Subsequence" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Minimum Path Sum ────────────────────────────────────────────── */
const parseGrid = str => str.split(';').map(row => row.split(',').map(s => Number(s.trim())).filter(v => !isNaN(v))).filter(r => r.length > 0);

const MinPathSumMode = () => {
  const [input, setInput] = useState('1,3,1;1,5,1;4,2,1');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const grid = parseGrid(input);
    if (grid.length === 0 || grid.length > 5 || grid[0].length === 0 || grid[0].length > 5) return;
    const m = grid.length, n = grid[0].length;
    const dp = Array.from({ length: m }, () => new Array(n).fill(null));
    const built = [];
    for (let i = 0; i < m; i++) {
      for (let j = 0; j < n; j++) {
        let val, note, from;
        if (i === 0 && j === 0) {
          val = grid[i][j];
          note = `dp[0][0] = grid[0][0] = ${val} — the starting cost.`;
        } else if (i === 0) {
          val = dp[i][j - 1] + grid[i][j];
          from = [[i, j - 1]];
          note = `dp[0][${j}] = dp[0][${j - 1}] + grid[0][${j}] = ${dp[i][j - 1]} + ${grid[i][j]} = ${val} — the top row can only arrive from the left.`;
        } else if (j === 0) {
          val = dp[i - 1][j] + grid[i][j];
          from = [[i - 1, j]];
          note = `dp[${i}][0] = dp[${i - 1}][0] + grid[${i}][0] = ${dp[i - 1][j]} + ${grid[i][j]} = ${val} — the left column can only arrive from above.`;
        } else {
          val = Math.min(dp[i - 1][j], dp[i][j - 1]) + grid[i][j];
          from = [[i - 1, j], [i, j - 1]];
          note = `dp[${i}][${j}] = min(dp[${i - 1}][${j}], dp[${i}][${j - 1}]) + grid[${i}][${j}] = min(${dp[i - 1][j]}, ${dp[i][j - 1]}) + ${grid[i][j]} = ${val}.`;
        }
        dp[i][j] = val;
        built.push({ grid: dp.map(r => [...r]), cell: [i, j], from, note });
      }
    }
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const grid = parseGrid(input);
  const emptyGrid = grid.length && grid.length <= 5 && grid[0].length <= 5 ? Array.from({ length: grid.length }, () => new Array(grid[0].length).fill(null)) : [[]];

  const code = {
    lines: [
      'int minPathSum(int[][] grid) {',
      '    int m = grid.length, n = grid[0].length;',
      '    int[][] dp = new int[m][n];',
      '    for (int i = 0; i < m; i++) {',
      '        for (int j = 0; j < n; j++) {',
      '            if (i == 0 && j == 0) dp[i][j] = grid[i][j];',
      '            else if (i == 0) dp[i][j] = dp[i][j - 1] + grid[i][j];',
      '            else if (j == 0) dp[i][j] = dp[i - 1][j] + grid[i][j];',
      '            else dp[i][j] = Math.min(dp[i - 1][j], dp[i][j - 1]) + grid[i][j];',
      '        }',
      '    }',
      '    return dp[m - 1][n - 1];',
      '}',
    ],
    active: !cur ? -1 : (cur.cell[0] === 0 && cur.cell[1] === 0) ? 5 : (cur.cell[0] === 0) ? 6 : (cur.cell[1] === 0) ? 7 : 8,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GridBoxes label="dp grid — minimum cost to reach each cell" grid={cur ? cur.grid : emptyGrid} getCellState={(r, c) => getFromCellState(cur, r, c)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={input} onChange={e => { setInput(e.target.value); setSteps(null); }} style={wideInput} placeholder="1,3,1;1,5,1;4,2,1" />
          <button onClick={run} style={smallBtn(V1)}>Build Grid</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter a grid as semicolon-separated rows (up to 5×5), then Build Grid.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Minimum Path Sum" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Edit Distance ───────────────────────────────────────────────── */
const EditDistanceMode = () => {
  const [word1, setWord1] = useState('horse');
  const [word2, setWord2] = useState('ros');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const w1 = word1.slice(0, 6), w2 = word2.slice(0, 6);
    if (w1.length === 0 || w2.length === 0) return;
    const m = w1.length, n = w2.length;
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(null));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    const built = [{ grid: dp.map(r => [...r]), cell: [0, 0], note: 'Base row/column: turning i characters into 0 characters (or vice versa) always costs exactly i deletions (or insertions).' }];
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (w1[i - 1] === w2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1];
          built.push({ grid: dp.map(r => [...r]), cell: [i, j], from: [[i - 1, j - 1]], note: `'${w1[i - 1]}' == '${w2[j - 1]}' — free! dp[${i}][${j}] = dp[${i - 1}][${j - 1}] = ${dp[i][j]} (no edit needed for this pair).` });
        } else {
          dp[i][j] = 1 + Math.min(dp[i - 1][j - 1], Math.min(dp[i - 1][j], dp[i][j - 1]));
          built.push({ grid: dp.map(r => [...r]), cell: [i, j], from: [[i - 1, j - 1], [i - 1, j], [i, j - 1]], note: `'${w1[i - 1]}' ≠ '${w2[j - 1]}' — dp[${i}][${j}] = 1 + min(replace=dp[${i - 1}][${j - 1}], delete=dp[${i - 1}][${j}], insert=dp[${i}][${j - 1}]) = 1 + min(${dp[i - 1][j - 1]}, ${dp[i - 1][j]}, ${dp[i][j - 1]}) = ${dp[i][j]}.` });
        }
      }
    }
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const w1 = word1.slice(0, 6), w2 = word2.slice(0, 6);
  const emptyGrid = Array.from({ length: w1.length + 1 }, () => new Array(w2.length + 1).fill(null));

  const code = {
    lines: [
      'int minDistance(String word1, String word2) {',
      '    int m = word1.length(), n = word2.length();',
      '    int[][] dp = new int[m + 1][n + 1];',
      '    for (int i = 0; i <= m; i++) dp[i][0] = i;',
      '    for (int j = 0; j <= n; j++) dp[0][j] = j;',
      '    for (int i = 1; i <= m; i++) {',
      '        for (int j = 1; j <= n; j++) {',
      '            if (word1.charAt(i - 1) == word2.charAt(j - 1)) {',
      '                dp[i][j] = dp[i - 1][j - 1];',
      '            } else {',
      '                dp[i][j] = 1 + Math.min(dp[i-1][j-1], Math.min(dp[i-1][j], dp[i][j-1]));',
      '            }',
      '        }',
      '    }',
      '    return dp[m][n];',
      '}',
    ],
    active: !cur ? -1 : (cur.from && cur.from.length === 1) ? 8 : 10,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GridBoxes label="dp grid — min edits to transform one prefix into the other" grid={cur ? cur.grid : emptyGrid} getCellState={(r, c) => getFromCellState(cur, r, c)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <input value={word1} onChange={e => { setWord1(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 110 }} placeholder="horse (max 6)" />
          <input value={word2} onChange={e => { setWord2(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 110 }} placeholder="ros (max 6)" />
          <button onClick={run} style={smallBtn(V1)}>Build Grid</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? `Enter two short words (max 6 chars each), then Build Grid.` : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Edit Distance" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const up = 1 + Math.floor(Math.random() * 10);
  const left = 1 + Math.floor(Math.random() * 10);
  const isCounting = Math.random() < 0.5;
  const correct = isCounting ? up + left : Math.min(up, left);
  let wrong = isCounting ? Math.max(up, left) : up + left;
  if (wrong === correct) wrong = correct + 1;
  const options = Math.random() < 0.5 ? [correct, wrong] : [wrong, correct];
  return { up, left, isCounting, correct, options };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '4px 0 14px', lineHeight: 1.6 }}>
        {scenario.isCounting
          ? `This is a PATH-COUNTING grid DP (like Unique Paths). dp[i-1][j] = ${scenario.up}, dp[i][j-1] = ${scenario.left}. What is dp[i][j]?`
          : `This is a MIN-COST grid DP (like Minimum Path Sum, ignoring the current cell's own cost for this question). dp[i-1][j] = ${scenario.up}, dp[i][j-1] = ${scenario.left}. What is dp[i][j] (before adding the current cell's cost)?`}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {scenario.options.map((opt, oi) => {
          const revealed = answer !== null;
          const isCorrect = opt === scenario.correct;
          const isPicked = opt === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={oi} onClick={() => pick(opt)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>dp[i][j] = {opt}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text)' }}>
            {scenario.isCounting ? 'Counting problems SUM their neighbors — every route through either one is distinct.' : 'Cost problems take the MIN (or max) of their neighbors — only the cheapest route in matters.'}
          </span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const Dp2DPlayground = () => {
  const [mode, setMode] = useState('uniquepaths');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'uniquepaths' && <UniquePathsMode />}
      {mode === 'lcs' && <LcsMode />}
      {mode === 'minpathsum' && <MinPathSumMode />}
      {mode === 'editdistance' && <EditDistanceMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default Dp2DPlayground;
